import { DateTime, Settings, Duration } from "luxon";
import {
  CalendarDate,
  parseDate,
  DateFormatter,
} from "@internationalized/date";
import React from "react";

import config from "../config";

export const dateFormats = {
  YMD: "yyyy/LL/dd",
  MDY: "LL/dd/yyyy",
  DMY: "dd/LL/yyyy",
};

export const TIME_FORMAT = "HH:mm:ss";

export const MISSING_TIMEZONE_ERROR =
  "Missing System Timezone. Check configuration file.";
export const INVALID_TIMESTAMP_ERROR = "Invalid timestamp.";
export const INVALID_NAN_TIMESTAMP_ERROR = "Invalid NaN timestamp.";

/**
 * Returns tokenized date format for other functions
 *
 * @export
 * @return {string} date format
 */
export function getDateFormat() {
  return dateFormats[config.dateFormat];
}

export function getDateRegex() {
  return getDateFormat()
    .replace(/LL/i, "[0,1]\\d")
    .replace(/DD/i, "[0-3]\\d")
    .replace(/YYYY/i, "\\d{4}");
}

/**
 * Returns the standard time format of HH:mm:ss
 *
 * @export
 * @return {string}
 */
export function getTimeFormat() {
  return TIME_FORMAT;
}

/**
 * Takes an AS1-formatted date and converts it back to ISO 8601
 *
 * @export
 * @param {string} timestamp AS1-formatted date
 * @return {string} ISO 8601 date formatted string
 */
export function convertFormattedDateToISO(timestamp) {
  return DateTime.fromFormat(timestamp, getDateFormat()).toISO();
}

/**
 * Converts an AS1 formatted date and time to ISO 8601 formatted string
 * @param {string} date AS1 Formatted date
 * @param {string} time hh:mm:ss formatted time
 * @returns {string} ISO 8601 formatted date
 */
export function convertFormattedDateTimeToISO(date, time) {
  const timeLuxon = DateTime.fromISO(time).toObject();
  const dateLuxon = DateTime.fromFormat(date, getDateFormat());
  const timeLuxonParams = {
    hour: timeLuxon.hour,
    minute: timeLuxon.minute,
    second: timeLuxon.second,
    millisecond: timeLuxon.millisecond,
  };
  return dateLuxon.set(timeLuxonParams).toISO();
}

/**
 * Returns a localised date formatted string and time with an optional string separator
 *
 * @export
 * @param {string} separator string separator wrapped in a space, "-" becomes " - "
 * @return {string}
 */
export function getDateTimeFormat(separator) {
  return [
    getDateFormat(),
    separator !== "" ? separator : undefined,
    getTimeFormat(),
  ]
    .filter(Boolean)
    .join(" ");
}

/**
 * Takes an ISO timestamp and returns a boolean value indicating whether the time is valid
 *
 * @export
 * @param {string} timestamp ISO 8601 timestamp
 * @return {boolean} true if the time is valid
 */
export function isValidTime(timestamp) {
  return DateTime.fromISO(timestamp).isValid;
}

/**
 * Takes an ISO 8601 timestamp and IANA-specified timezone and returns a timeshifted value
 *
 * @export
 * @param {string} timestamp ISO 8601 timestamp
 * @param {string} timezone IANA-specified timezone
 * @return {DateTime} Luxon DateTime object offset by timezone
 */
export function calibrateTimestampToTimezone(timestamp, timezone) {
  const tz = timezone || config.timezone;
  return DateTime.fromISO(timestamp).setZone(tz);
}

/**
 * Formats a timestamp into a human-readable date
 *
 * @export
 * @param {string} timestamp ISO 8601 timestamp
 * @param {string} timezone IANA-specified timezone
 * @return {string}
 */
export function formatDate(timestamp, timezone) {
  const tz = timezone || config.timezone;
  if (!timestamp) {
    return "";
  }
  if (tz) {
    return calibrateTimestampToTimezone(timestamp, tz).toFormat(
      getDateFormat(),
    );
  } else {
    throw new Error(MISSING_TIMEZONE_ERROR);
  }
}

/**
 * Formats an ISO 8601 timestamp to a formatted time
 * Additionally, affected by config.timezone offset
 *
 * @export
 * @param {string} timestamp ISO 8601 timestamp
 * @param {string} timezone IANA-specified timezone
 * @return {string} HH:mm:ss formatted local time or UTC time offset by timezone
 */
export function formatTime(timestamp, timezone) {
  const tz = timezone || config.timezone;
  if (!timestamp) {
    return "";
  }
  if (tz) {
    return calibrateTimestampToTimezone(timestamp, tz).toFormat(
      getTimeFormat(),
    );
  } else {
    throw new Error(MISSING_TIMEZONE_ERROR);
  }
}

/**
 * Converts a timestamp to a formatted time without seconds
 * Additionally, affected by config.timezone offset
 *
 * @export
 * @param {string} timestamp timestamp
 * @return {string} HH:mm formatted local time or UTC time offset by timezone
 */
export function formatTimeWithoutSeconds(timestamp) {
  return DateTime.fromISO(timestamp).toFormat("HH:mm");
}

/**
 * Takes an ISO 8601 timestamp and returns both the date and time according to the config format.
 * Additionally, affected by the config.timezone
 *
 * @export
 * @param {string} timestamp ISO 8601 timestamp
 * @param {string} separator string to separate the values, ex: using a `-` in between the date and time
 * @param {string} timezone IANA-specified timezone
 * @return {string} local date and time, or UTC time offset by timezone
 */
export function formatDateTime(timestamp, separator = "-", timezone) {
  const tz = timezone || config.timezone;
  if (!timestamp) {
    return "";
  }
  if (tz) {
    return calibrateTimestampToTimezone(timestamp, tz).toFormat(
      getDateTimeFormat(separator),
    );
  } else {
    throw new Error(MISSING_TIMEZONE_ERROR);
  }
}

/**
 * Takes an ISO 8601 timestamp and returns both the date and time
 * according to the config format, without seconds.
 * Additionally, affected by the config.timezone
 *
 * @export
 * @param {string} timestamp ISO 8601 timestamp
 * @param {string} separator string to separate the values, ex: using a `-` in between the date and time
 * @param {string} timezone IANA-specified timezone
 * @return {string} local date and time, or UTC time offset by timezone, without seconds
 */
export function formatDateTimeWithoutSeconds(
  timestamp,
  separator = "-",
  timezone,
) {
  const datetime = formatDateTime(timestamp, separator, timezone);
  if (datetime && datetime.length > 3) {
    return datetime.substring(0, datetime.length - 3);
  }
}

/**
 * Detects if an object is a JS Date object
 * @return {boolean}
 * @param obj
 */
export function isDateObject(obj) {
  return (
    typeof obj === "object" &&
    Object.prototype.toString.call(obj) === "[object Date]"
  );
}

/**
 * Returns the type of object
 * @param {any} obj Any object that should be evaluated
 * @returns {'undefined'|'null'|'string'|'number'|'zero'|'luxon'|'jsdate'|'NaN'|typeof obj} type of object
 */
export function detectedType(obj) {
  const tsType = typeof obj;
  let type = tsType;
  if (typeof obj === "undefined") {
    type = "undefined";
  } else if (obj === null && obj !== 0) {
    type = "null";
  } else if (obj.isLuxonDateTime) {
    type = "luxon";
  } else if (tsType === "string") {
    type = "string";
  } else if (tsType === "number") {
    if (!isNaN(obj)) {
      type = obj !== 0 && obj > 0 ? "number" : "zero";
    } else {
      type = "NaN";
    }
  } else if (isDateObject(obj)) {
    type = "jsdate";
  }

  return type;
}

/**
 * Takes a ISO 8601 timestamp and returns a Luxon DateTime object.
 * This is handy if you don't know if you're passing around a Luxon object or a raw timestamp.
 *
 * 1. Consider all inbound dates as ISO 8601 compliant
 * 2. Internally pass Luxon DateTime objects
 * 3. Finally, return ISO 8601 formatted strings for when you need to output to the API.
 *
 * If you aren't sure what format the date will be in,
 * @export
 * @param {any} timestamp an unknown date format
 * @param {boolean} isUTC true if source timestamp is in UTC and returned time should also be in UTC
 * @return {DateTime} A Luxon DateTime object
 */
export function toLuxon(timestamp, isUTC) {
  switch (detectedType(timestamp)) {
    case "undefined":
      if (config.debug) console.warn("Received Undefined Timestamp", timestamp);
      return !isUTC ? DateTime.local() : DateTime.utc();
    case "null":
      if (config.debug) console.warn("Received Null Timestamp", timestamp);
      return !isUTC ? DateTime.local() : DateTime.utc();
    case "luxon":
      return timestamp;
    case "string":
      return !isUTC
        ? DateTime.fromISO(timestamp)
        : DateTime.fromISO(timestamp, { zone: "utc" });
    case "number":
      return !isUTC
        ? DateTime.fromMillis(timestamp)
        : DateTime.fromMillis(timestamp, { zone: "utc" });
    case "jsdate":
      return !isUTC
        ? DateTime.fromJSDate(timestamp)
        : DateTime.fromJSDate(timestamp, { zone: "utc" });
    case "NaN":
      throw new Error(INVALID_NAN_TIMESTAMP_ERROR);
    case "zero":
      return !isUTC
        ? DateTime.fromMillis(0)
        : DateTime.fromMillis(0, { zone: "utc" });
    default:
      throw new Error(INVALID_TIMESTAMP_ERROR);
  }
}
/**
 *
 * @param {string} timezone IANA timezone
 */
export function setDefaultTimezone(timezone) {
  const tz = timezone || config.timezone;
  if (tz) {
    Settings.defaultZoneName = tz;
  } else {
    throw new Error(MISSING_TIMEZONE_ERROR);
  }
}

/**
 * Returns object from array with oldest timestamp key
 * @param {array} array of objects containing `timestamp` key
 * @return {DateTime} Luxon DateTime object
 */
export const minByTimestamp = (array) =>
  DateTime.fromMillis(
    Math.min.apply(
      Math,
      array.map(function (o) {
        return toLuxon(o.timestamp, true);
      }),
    ),
  ).setZone("UTC");

/**
 * Returns object from array with newest timestamp key
 * @param {array} array of objects containing `timestamp` key
 * @return {DateTime} Luxon DateTime object
 */
export const maxByTimestamp = (array) =>
  DateTime.fromMillis(
    Math.max.apply(
      Math,
      array.map(function (o) {
        return toLuxon(o.timestamp, true);
      }),
    ),
  ).setZone("UTC");

/**
 * @description Generates a string that represents the difference between chartEndTime and the current time.
 * @export
 * @param {Duration} duration
 * @returns {string} Time formatted as hh:mm:ss unless Duration is greater than one day, then (days)d hh:mm:ss
 */
export function formatDuration(duration) {
  if (!duration) return "";

  let _duration = duration;
  const milliseconds = duration.toMillis();
  let isNegative = false;
  if (milliseconds < 0) {
    _duration = Duration.fromMillis(Math.abs(milliseconds));
    isNegative = true;
  }

  const days = Math.floor(_duration.as("days"));
  const minutes = Math.floor(_duration.as("minutes"));
  const seconds = Math.floor(_duration.as("seconds"));
  const absoluteDuration = (function formatAbsoluteTime() {
    if (days) return `${_duration.toFormat("d% hh:mm:ss").replace("%", "d")}`;
    if (minutes) return _duration.toFormat("hh:mm:ss");
    return `${_duration.toFormat("s")} second${
      seconds === 1 ? "" : "s"
    }`.replaceAll("-", "");
  })(_duration);

  const suffix = isNegative ? "from now" : "ago";
  return `${absoluteDuration} ${suffix}`;
}

/**
 *
 * @param {*} date
 * @returns short formatted "time ago"
 */
export function timeSince(date) {
  const seconds = Math.floor((new Date() - date) / 1000);
  let interval = seconds / 31536000;

  if (interval > 1) {
    return Math.floor(interval) + " year ago";
  }
  interval = seconds / 2592000;
  if (interval > 1) {
    return Math.floor(interval) + " month ago";
  }
  interval = seconds / 86400;
  if (interval > 1) {
    return Math.floor(interval) + " day ago";
  }
  interval = seconds / 3600;
  if (interval > 1) {
    return Math.floor(interval) + " hour ago";
  }
  interval = seconds / 60;
  if (interval > 1) {
    return Math.floor(interval) + " min ago";
  }
  return Math.floor(seconds) + " sec ago";
}

/**
 * @description Generates time scale JSX.
 * @export
 * @param {DateTime} startTime Start time.
 * @param {DateTime} endTime End time.
 * @returns {JSX} Time scale JSX
 */
export function TimeScale({ startTime, endTime }) {
  const isValidDates = startTime?.isValid && endTime?.isValid;
  return (
    <div className="time-scale">
      {isValidDates ? (
        <>
          <span>{formatTimeWithoutSeconds(startTime)}</span>
          <span>{parseInt(endTime.diff(startTime).as("minutes"))} min</span>
          <span>{endTime.toFormat("MM/dd - HH:mm")}</span>
        </>
      ) : null}
    </div>
  );
}

export const localeMap = {
  MDY: "en-US",
  DMY: "en-GB",
  YMD: "ja-JP",
};
/**
 * Creates a CalendarDate object, which represents a specific date that won't change based on timezone.
 * Necessary for Aria datePickers, and useful for DOB.
 * see https://react-spectrum.adobe.com/internationalized/date/CalendarDate.html
 * @param {any} date It will parse String, Date, and Luxon formats.
 * @returns CalendarDate | falsy input
 */
export function toCalendarDate(date) {
  try {
    if (!date) return date;
    if (date instanceof CalendarDate) return date;
    // String
    if (date.split) return parseDate(date.split("T")[0]);
    // native Date
    if (date.toISOString) return parseDate(date.toISOString().split("T")[0]);
    // Luxon.DateTime
    if (date.isValid) return parseDate(date.toFormat("yyyy-MM-dd")); // Luxon
  } catch (e) {
    console.error('toCalendarDate error for:"', date, '"', e);
  }
}

export const formatter = {
  init() {
    let locale;
    if (config.dateFormat === "MDY") {
      locale = "en-US";
    } else if (config.dateFormat === "DMY") {
      locale = "en-GB";
    } else if (config.dateFormat === "YMD") {
      locale = "ja-JP";
    } else {
      throw new Error("Invalid dateFormat in config.json");
    }

    const dateOptions = {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    };
    const timeOptions = {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
      timeZone: config.timezone,
    };

    const dateTimeFormat = new DateFormatter(locale, {
      ...dateOptions,
      ...timeOptions,
    });
    const calendarDateFormat = new DateFormatter(locale, {
      dateOptions,
      timeZone: "Zulu",
    });
    const timeFormat = new DateFormatter(locale, timeOptions);
    const dateFormat = new DateFormatter(locale, {
      ...dateOptions,
      timeZone: config.timezone,
    });
    Object.assign(this, {
      dateFormat,
      timeFormat,
      dateTimeFormat,
      calendarDateFormat,
    });
    return this;
  },
  format: (date, _formatter = formatter.dateTimeFormat) => {
    if (!date) return;
    try {
      let formatted = _formatter?.format(new Date(date));
      return fix2digitDate(formatted);
    } catch (e) {
      console.error("formatter error", date, e);
    }
  },
  date: (date) => formatter.format(date, formatter.dateFormat),
  time: (time) => formatter.format(time, formatter.timeFormat),
  calendarDate: (d) => formatter.format(d, formatter.calendarDateFormat),
};

// There is a bug in Chrome, where 2-digit doesn't work on dates.
// We can remove this fix after this bug is closed.
// https://bugs.chromium.org/p/chromium/issues/detail?id=527926
function fix2digitDate(dateStr) {
  return dateStr?.replaceAll(/\d+/g, (s) => s.padStart(2, 0));
}
